/**
 * UsuarioFormDialog (F84) — crear/editar usuario.
 *
 * Modo crear: pide email, nombre, rol, password, activo, password_change_required.
 * Modo editar: solo email, nombre, rol, activo (la password va por "Regenerar").
 */
import { useEffect, useState } from "react";
import { apexDe } from "@/lib/branding";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

import {
  userCreateSchema, userUpdateSchema,
  type User, type UserCreateInput, type UserCreatedResponse, type UserUpdateInput,
} from "./schemas";
import { useCrearUsuario, useEditarUsuario, useRoles } from "./api";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  usuario: User | null;
  onCreated: (res: UserCreatedResponse | null) => void;
};

const CREATE_DEFAULTS: UserCreateInput = {
  email: "",
  nombre_completo: "",
  rol_id: "",
  password: "",
  activo: true,
  password_change_required: true,
};

function generarPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  return Array.from({ length: 14 }, () =>
    chars[Math.floor(Math.random() * chars.length)],
  ).join("");
}

export function UsuarioFormDialog({ open, onOpenChange, usuario, onCreated }: Props) {
  const editMode = usuario !== null;
  // F104.1 — guard: si edita su propio user, deshabilita rol y activo
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = editMode && usuario && currentUser?.id === usuario.id;
  const { data: roles = [] } = useRoles();
  const crearMut = useCrearUsuario();
  const editarMut = useEditarUsuario(usuario?.id ?? "");

  const [error, setError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);

  // En modo crear usamos schema de create; en modo editar usamos update
  const form = useForm<UserCreateInput | UserUpdateInput>({
    resolver: zodResolver(editMode ? userUpdateSchema : userCreateSchema),
    defaultValues: editMode
      ? {
          email: usuario.email,
          nombre_completo: usuario.nombre_completo ?? "",
          rol_id: usuario.rol_id ?? "",
          activo: usuario.activo,
        }
      : CREATE_DEFAULTS,
  });
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = form;

  useEffect(() => {
    if (open) {
      setError(null);
      setShowPass(false);
      reset(editMode ? {
        email: usuario.email,
        nombre_completo: usuario.nombre_completo ?? "",
        rol_id: usuario.rol_id ?? "",
        activo: usuario.activo,
      } : CREATE_DEFAULTS);
    }
  }, [open, editMode, usuario, reset]);

  const onSubmit: SubmitHandler<UserCreateInput | UserUpdateInput> = async (raw) => {
    setError(null);
    try {
      if (editMode) {
        // En editar limpia campos vacíos
        const cleaned: UserUpdateInput = {};
        if (raw.email && raw.email !== usuario.email) cleaned.email = raw.email;
        if (raw.nombre_completo !== undefined && raw.nombre_completo !== (usuario.nombre_completo ?? ""))
          cleaned.nombre_completo = raw.nombre_completo as string || undefined;
        if (raw.rol_id && raw.rol_id !== usuario.rol_id) cleaned.rol_id = raw.rol_id as string;
        if (raw.activo !== undefined && raw.activo !== usuario.activo) cleaned.activo = raw.activo as boolean;
        await editarMut.mutateAsync(cleaned);
        toast.success("Usuario actualizado");
        onCreated(null);
      } else {
        const res = await crearMut.mutateAsync(raw as UserCreateInput);
        toast.success(`Usuario ${res.user.email} creado`);
        onCreated(res);
      }
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editMode ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              {editMode
                ? "Cambia email, nombre, rol o activa/desactiva. Para password usa el botón Regenerar."
                : "Crea cuenta de admin del sistema. Asigna un rol base."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div>
              <Label>Email *</Label>
              <Input type="email" {...register("email")} placeholder={`usuario@${apexDe()}`} />
              {errors.email && <span className="text-xs text-destructive">{errors.email.message as string}</span>}
            </div>
            <div>
              <Label>Nombre completo</Label>
              <Input {...register("nombre_completo")} placeholder="Juan Pérez" />
            </div>
            <div>
              <Label>Rol *</Label>
              <Select {...register("rol_id")} disabled={!!isSelf}>
                <option value="">Selecciona un rol…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre} {r.descripcion ? `— ${r.descripcion}` : ""}
                  </option>
                ))}
              </Select>
              {isSelf && (
                <span className="text-[10px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1 mt-0.5">
                  <Lock size={10} /> No puedes cambiar tu propio rol
                </span>
              )}
              {errors.rol_id && <span className="text-xs text-destructive">{errors.rol_id.message as string}</span>}
            </div>

            {!editMode && (
              <>
                <div>
                  <Label>Password inicial *</Label>
                  <div className="flex gap-1">
                    <div className="relative flex-1">
                      <Input
                        type={showPass ? "text" : "password"}
                        {...register("password")}
                        placeholder="Mínimo 10: mayúscula, minúscula, número y símbolo"
                        className="pr-8"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                        tabIndex={-1}
                      >
                        {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => {
                        const p = generarPassword();
                        setValue("password", p, { shouldDirty: true, shouldValidate: true });
                        setShowPass(true);
                      }}
                    >
                      Generar
                    </Button>
                  </div>
                  {errors.password && (
                    <span className="text-xs text-destructive">{errors.password.message as string}</span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    Solo la verás una vez tras crear. Pásasela al usuario.
                  </span>
                </div>
                <div>
                  <Label>
                    <input
                      type="checkbox"
                      {...register("password_change_required")}
                      className="mr-1.5 h-3 w-3 align-middle"
                      defaultChecked
                    />
                    Forzar cambio de password en primer login
                  </Label>
                </div>
              </>
            )}

            <div>
              <Label>
                <input
                  type="checkbox"
                  {...register("activo")}
                  className="mr-1.5 h-3 w-3 align-middle"
                  defaultChecked
                  disabled={!!isSelf}
                />
                Usuario activo (puede entrar al sistema)
              </Label>
              {isSelf && (
                <span className="text-[10px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1 mt-0.5 ml-5">
                  <Lock size={10} /> No puedes desactivar tu propia cuenta
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={crearMut.isPending || editarMut.isPending}>
            {crearMut.isPending || editarMut.isPending ? "Guardando…" :
             editMode ? "Guardar cambios" : "Crear usuario"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
